import { useState, useEffect } from 'react';
import { getClients, createClient, updateClient, deleteClient, downloadClientReport, reassignClient, getTeamMembers, getMyAgencies } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Trash2, Building2, Mail, Phone, Search, FileDown, Pencil, UserCheck, Copy, Check, UserCog, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const CLIENT_TYPES = [
  { value: 'marketalert', label: 'MarketAlert', desc: '19€/mois (engagement)', color: '#397DFF' },
  { value: 'marketalert_no_engagement', label: 'MarketAlert', desc: '29€/mois (sans engagement)', color: '#397DFF' },
  { value: 'serenite', label: 'Sérénité', desc: 'Accompagné', color: '#10D183' },
  { value: 'pro', label: 'Pro', desc: 'Accompagné', color: '#10D183' },
  { value: 'starter', label: 'Starter', desc: 'Accompagné', color: '#10D183' },
  { value: 'elite', label: 'Elite', desc: 'Accompagné', color: '#10D183' },
];

const getTypeLabel = (type) => {
  const t = CLIENT_TYPES.find(ct => ct.value === type);
  if (!t) return 'MarketAlert';
  if (type === 'marketalert') return 'MarketAlert 19€';
  if (type === 'marketalert_no_engagement') return 'MarketAlert 29€';
  return t.label;
};

const ClientsPage = () => {
  const { isTeamMember } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [credentialsDialog, setCredentialsDialog] = useState(null);
  const [copied, setCopied] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    company: '',
    phone: '',
    give_access: false,
    client_type: 'marketalert',
    agency_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assignDialog, setAssignDialog] = useState(null);

  useEffect(() => {
    fetchClients();
    getMyAgencies().then(res => setAgencies(res.data)).catch(() => {});
    if (!isTeamMember) {
      getTeamMembers().then(res => setTeamMembers(res.data)).catch(() => {});
    }
  }, [isTeamMember]);

  const fetchClients = async () => {
    try {
      const response = await getClients();
      setClients(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingClient(null);
    setFormData({ email: '', name: '', company: '', phone: '', give_access: false, client_type: 'marketalert', agency_id: agencies[0]?.id || '' });
    setDialogOpen(true);
  };

  const openEdit = (client) => {
    setEditingClient(client);
    setFormData({
      email: client.email || '',
      name: client.name || '',
      company: client.company || '',
      phone: client.phone || '',
      give_access: false,
      client_type: client.client_type || 'marketalert',
      agency_id: client.agency_id || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingClient) {
        const { give_access, ...updateData } = formData;
        await updateClient(editingClient.id, updateData);
        toast.success('Client modifié avec succès !');
      } else {
        const res = await createClient(formData);
        toast.success('Client ajoute avec succes !');
        if (res.data.temp_password) {
          setCredentialsDialog({ email: formData.email, password: res.data.temp_password });
        }
      }
      setDialogOpen(false);
      setEditingClient(null);
      setFormData({ email: '', name: '', company: '', phone: '', give_access: false, client_type: 'marketalert', agency_id: '' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) return;
    try {
      await deleteClient(id);
      toast.success('Client supprimé');
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleReassign = async (clientId, memberId) => {
    try {
      await reassignClient(clientId, memberId);
      toast.success('Client réassigné');
      setAssignDialog(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const getMemberName = (memberId) => {
    const m = teamMembers.find(t => t.id === memberId);
    return m ? m.name : null;
  };

  const handleDownloadReport = async (clientId, clientName) => {
    try {
      toast.info('Génération du rapport...');
      const response = await downloadClientReport(clientId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport_${clientName.replace(/\s/g, '_')}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Rapport téléchargé !');
    } catch (error) {
      toast.error('Erreur lors de la génération du rapport');
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6" data-testid="clients-loading">
        <Skeleton className="h-10 w-48 bg-white" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-40 bg-white" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#191D1E]">Clients</h1>
          <p className="text-[#687175] mt-1">{clients.length} client(s) enregistré(s)</p>
        </div>
        <Button className="btn-primary" onClick={openCreate} data-testid="add-client-btn">
          <Plus className="w-4 h-4 mr-2" /> Ajouter un client
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#939BA0]" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white border-[#BFC6CC] text-[#191D1E]"
          placeholder="Rechercher un client..."
          data-testid="client-search-input"
        />
      </div>

      {/* Dialog Create/Edit */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingClient(null); }}>
        <DialogContent className="bg-white border-[#BFC6CC]/30">
          <DialogHeader>
            <DialogTitle className="text-[#191D1E]">
              {editingClient ? 'Modifier le client' : 'Nouveau client'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-[#191D1E]">Nom complet</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]"
                placeholder="Jean Dupont"
                required
                data-testid="client-name-input"
              />
            </div>
            <div>
              <Label className="text-[#191D1E]">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]"
                placeholder="client@entreprise.com"
                required
                data-testid="client-email-input"
              />
            </div>
            <div>
              <Label className="text-[#191D1E]">Entreprise</Label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]"
                placeholder="Nom de l'entreprise"
                required
                data-testid="client-company-input"
              />
            </div>
            <div>
              <Label className="text-[#191D1E]">Téléphone (optionnel)</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]"
                placeholder="06 12 34 56 78"
                data-testid="client-phone-input"
              />
            </div>
            {/* Type de client */}
            <div>
              <Label className="text-[#191D1E]">Type de client</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {CLIENT_TYPES.map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setFormData({ ...formData, client_type: opt.value })}
                    className={`p-3 rounded-xl border text-left transition-colors ${
                      formData.client_type === opt.value
                        ? 'border-[#397DFF] bg-[#397DFF]/5'
                        : 'border-[#BFC6CC]/20 hover:bg-[#F5F6F9]'
                    }`}
                    data-testid={`client-type-${opt.value}`}
                  >
                    <p className="text-sm font-medium text-[#191D1E]">{opt.label}</p>
                    <p className="text-[10px] text-[#687175]">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            {/* Agence */}
            {agencies.length > 0 && (
              <div>
                <Label className="text-[#191D1E]">Agence de rattachement</Label>
                <select value={formData.agency_id}
                  onChange={(e) => setFormData({ ...formData, agency_id: e.target.value })}
                  className="w-full mt-1.5 p-2.5 rounded-lg bg-[#F5F6F9] border border-[#BFC6CC] text-[#191D1E] text-sm focus:ring-2 focus:ring-[#397DFF]/30 focus:border-[#397DFF] outline-none"
                  data-testid="client-agency-select">
                  <option value="">— Aucune agence —</option>
                  {agencies.map(a => (
                    <option key={a.id} value={a.id}>{a.name} — {a.city}</option>
                  ))}
                </select>
              </div>
            )}
            {!editingClient && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F5F6F9] border border-[#BFC6CC]/20">
                <input
                  type="checkbox"
                  id="give_access"
                  checked={formData.give_access}
                  onChange={(e) => setFormData({ ...formData, give_access: e.target.checked })}
                  className="w-4 h-4 rounded border-[#BFC6CC] text-[#397DFF] focus:ring-[#397DFF]"
                  data-testid="client-access-checkbox"
                />
                <label htmlFor="give_access" className="text-sm text-[#3C4649] cursor-pointer">
                  <span className="font-medium text-[#191D1E]">Donner un accès client</span>
                  <br />
                  <span className="text-xs text-[#687175]">Crée un espace client avec identifiants de connexion</span>
                </label>
              </div>
            )}
            <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="client-submit-btn">
              {submitting ? (editingClient ? 'Modification...' : 'Création...') : (editingClient ? 'Enregistrer' : 'Créer le client')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials dialog */}
      <Dialog open={!!credentialsDialog} onOpenChange={() => setCredentialsDialog(null)}>
        <DialogContent className="bg-white border-[#BFC6CC]/30">
          <DialogHeader>
            <DialogTitle className="text-[#191D1E]">Accès client créé</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#687175] mt-2">
            Communiquez ces identifiants au client. Il pourra se connecter a son espace.
          </p>
          <div className="mt-4 space-y-3">
            <div className="bg-[#F5F6F9] rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-[#687175]">Email</p>
                <p className="font-mono text-[#191D1E]">{credentialsDialog?.email}</p>
              </div>
              <button onClick={() => copyToClipboard(credentialsDialog?.email, 'email')} className="p-2 hover:bg-white rounded-lg transition-colors">
                {copied === 'email' ? <Check className="w-4 h-4 text-[#10D183]" /> : <Copy className="w-4 h-4 text-[#687175]" />}
              </button>
            </div>
            <div className="bg-[#F5F6F9] rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-[#687175]">Mot de passe provisoire</p>
                <p className="font-mono text-[#191D1E]">{credentialsDialog?.password}</p>
              </div>
              <button onClick={() => copyToClipboard(credentialsDialog?.password, 'password')} className="p-2 hover:bg-white rounded-lg transition-colors">
                {copied === 'password' ? <Check className="w-4 h-4 text-[#10D183]" /> : <Copy className="w-4 h-4 text-[#687175]" />}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-12 text-center hover:shadow-md transition-shadow duration-200">
          <Building2 className="w-12 h-12 text-[#BFC6CC] mx-auto mb-4" />
          <p className="text-[#687175]">
            {searchTerm ? 'Aucun client trouvé' : 'Aucun client pour le moment'}
          </p>
          {!searchTerm && (
            <p className="text-sm text-[#939BA0] mt-1">
              Commencez par ajouter votre premier client
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client, index) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-5 hover:shadow-md transition-shadow duration-200"
              data-testid={`client-card-${client.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#397DFF]/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#397DFF]" />
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEdit(client)}
                    className="p-2 text-[#397DFF] hover:bg-[#397DFF]/10 rounded-lg transition-colors"
                    title="Modifier le client"
                    data-testid={`edit-client-${client.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {!isTeamMember && teamMembers.length > 0 && (
                    <button
                      onClick={() => setAssignDialog(client)}
                      className="p-2 text-[#397DFF] hover:bg-[#397DFF]/10 rounded-lg transition-colors"
                      title="Réassigner"
                      data-testid={`assign-client-${client.id}`}
                    >
                      <UserCog className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDownloadReport(client.id, client.name)}
                    className="p-2 text-[#397DFF] hover:bg-[#397DFF]/10 rounded-lg transition-colors"
                    title="Télécharger le rapport"
                    data-testid={`download-report-${client.id}`}
                  >
                    <FileDown className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(client.id)}
                    className="p-2 text-[#939BA0] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer le client"
                    data-testid={`delete-client-${client.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <h3 className="font-bold text-[#191D1E]">{client.name}</h3>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <p className="text-sm text-[#397DFF]">{client.company}</p>
                {client.has_access && (
                  <span className="text-[10px] font-semibold bg-[#10D183]/10 text-[#10D183] px-2 py-0.5 rounded-full flex items-center gap-1">
                    <UserCheck className="w-3 h-3" /> Accès
                  </span>
                )}
                {client.client_type && client.client_type !== 'marketalert' && client.client_type !== 'marketalert_no_engagement' ? (
                  <span className="text-[10px] font-semibold bg-[#10D183]/10 text-[#10D183] px-2 py-0.5 rounded-full">
                    {getTypeLabel(client.client_type)}
                  </span>
                ) : (
                  <span className="text-[10px] font-semibold bg-[#397DFF]/10 text-[#397DFF] px-2 py-0.5 rounded-full">
                    {getTypeLabel(client.client_type)}
                  </span>
                )}
                {client.agency_id && agencies.find(a => a.id === client.agency_id) && (
                  <span className="text-[10px] font-medium bg-[#F5F6F9] text-[#687175] px-2 py-0.5 rounded-full flex items-center gap-1">
                    <MapPin className="w-2.5 h-2.5" /> {agencies.find(a => a.id === client.agency_id)?.name}
                  </span>
                )}
                {!isTeamMember && client.created_by && getMemberName(client.created_by) && (
                  <span className="text-[10px] font-medium bg-[#397DFF]/10 text-[#397DFF] px-2 py-0.5 rounded-full">
                    {getMemberName(client.created_by)}
                  </span>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-[#687175]">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{client.email}</span>
                </div>
                {client.phone && (
                  <div className="flex items-center gap-2 text-sm text-[#687175]">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span>{client.phone}</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Reassign dialog */}
      <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
        <DialogContent className="bg-white border-[#BFC6CC]/30">
          <DialogHeader>
            <DialogTitle className="text-[#191D1E]">Réassigner {assignDialog?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#687175] mt-2 mb-4">Choisissez le collaborateur qui gèrera ce client :</p>
          <div className="space-y-2">
            {teamMembers.map(m => (
              <button key={m.id} onClick={() => handleReassign(assignDialog?.id, m.id)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-colors flex items-center gap-3 ${
                  assignDialog?.created_by === m.id ? 'border-[#397DFF] bg-[#397DFF]/5' : 'border-[#BFC6CC]/20 hover:bg-[#F5F6F9]'
                }`} data-testid={`assign-to-${m.id}`}>
                <div className="w-8 h-8 rounded-full bg-[#397DFF]/10 flex items-center justify-center">
                  <span className="text-xs font-bold text-[#397DFF]">{m.name?.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#191D1E]">{m.name}</p>
                  <p className="text-xs text-[#687175]">{m.email}</p>
                </div>
                {assignDialog?.created_by === m.id && <span className="ml-auto text-xs text-[#397DFF] font-medium">Actuel</span>}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
